
import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { colorToString } from '../widget.helper';
import { WidgetsContext } from './../../../index';
import { isMobile } from '../../../services/device';
import { ImageKitContext } from './../core/context';
import { POSITION, SCALE } from '../../../constants/background';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';
import useDelayedInView from '../../../components/DelayInView';
import { useInteractiveContent } from '../InteractiveContent/v2';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import styles from './background.module.scss';


export const BackgroundViewComponent = ({ className, background, style, breakpoint, isIcPageBackground }) => {
  if (background == null) {
    return false;
  }

  const type = get(background, 'type');
  const color = get(background, 'color');
  const blur = get(background, 'blur', 0);
  const colorOverlay = getColorsConfig(color);

  return (
    <div className={cx(styles.background, className)} style={style}>
      <div className={cx(styles.overlay, styles.color)} style={{
        background: colorOverlay,
        WebkitBackdropFilter:`blur(${blur}px)`,
        backdropFilter: type === 'glass' ? `blur(${blur}px)` : 'none'
      }}></div>

      {type === 'image' &&
        <ImageView background={background} isIcPageBackground={isIcPageBackground} />
      }

      {type === 'video' &&
        <VideoView background={background} breakpoint={breakpoint} />
      }
    </div>
  );
};

const backgroundViewEquals = function (prevProps, nextProps) {
  const paths = ['className', 'background', 'style'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const BackgroundView = React.memo(BackgroundViewComponent, backgroundViewEquals);

export const ImageViewComponent = ({ background, imageCDN, isIcPageBackground }) => {
  const context = React.useContext(WidgetsContext);
  const icV2Context = useInteractiveContent();
  const imageKitCDN = React.useContext(ImageKitContext);
  const media = get(background, 'media');
  const mobile = isMobile();

  if (!media) {
    return null;
  }

  const finalImageCDN = context.imageCDN || imageCDN || icV2Context?.imageCdn || imageKitCDN;
  const backgroundStyles = getImageBackgroundStyles(finalImageCDN, background, isIcPageBackground, mobile);

  return (
    <div className={cx(styles.overlay, styles.image)} style={backgroundStyles }/>
  );
}

const imageViewEquals = function (prevProps, nextProps) {
  const paths = ['background'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const ImageView = React.memo(ImageViewComponent, imageViewEquals);

export const VideoViewComponent = ({ background, breakpoint, imageCDN }) => {
  const context = React.useContext(WidgetsContext);
  const icV2Context = useInteractiveContent();
  const imageKitCDN = React.useContext(ImageKitContext);
  const media = get(background, 'media');
  const [show, setShow] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);

  if (!media) {
    return null;
  }

  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);

  const [ref] = useDelayedInView({
    rootMargin: '0px',
    root: null,
    onChange: (visible) => {
      setShow(visible);
    }
  });

  const setVideoRefs = React.useCallback((node) => {
      videoContainerRef.current = node;
      ref(node);
    },
    [ref],
  );

  const url = media.url;
  const backgroundStyles = getVideoBackgroundStyles(background);

  const finalImageCDN = context.imageCDN || imageCDN || icV2Context?.imageCdn || imageKitCDN;
  const posterStyles = getVideoPosterStyles(background, `${finalImageCDN}/${url}/ik-thumbnail.jpg`);

  const calculateSource = (breakpoint) => {
    if(!videoContainerRef.current){
      return "";
    }

    var videoUrl = `${finalImageCDN}/${url}`;
    if (breakpoint === 'mobile') {
      var height =  videoContainerRef.current.offsetHeight;
      var maxHeight = get(background, 'media.height') || 1080;

      if (height <= 360) {
        height = 360;
      } else if (height <= 480) {
        height = 480;
      } else if (height <= 720) {
        height = 720;
      } else {
        height = 1080;
      }

      height = Math.min(height, maxHeight);

      if (height >= 360) {
        videoUrl = `${videoUrl}?tr=h-${height}`;
      }
    }

    return videoUrl;
  }

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const url = calculateSource(breakpoint);
    videoRef.current.src = url;
  }, [videoContainerRef.current, breakpoint]);

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  const opacityConditon = posterStyles?.opacity != 1;
  const isVideoLoaded = show && videoLoaded;

  return (
    <div ref={setVideoRefs} className={cx(styles.overlay, styles.video)}>
      <div
        className={cx(styles.poster, { 
          [styles.hidden]: (isVideoLoaded && opacityConditon), 
          [styles.conditonalHide]: (isVideoLoaded && !opacityConditon) 
        })}
        style={{
          ...posterStyles,
          opacity: opacityConditon 
            ? Math.max(0, (posterStyles?.opacity || 0) - 0.10) 
            : posterStyles?.opacity,
          transition: opacityConditon ? 'opacity 0.5s ease-in-out' : 'none',
        }}
      >
        &nbsp;
      </div>
      {show &&
        <video
          key={`${breakpoint}-${url}`}
          style={backgroundStyles}
          ref={videoRef}
          playsInline
          preload={breakpoint == 'mobile' ? 'auto' : 'auto'}
          className={cx(styles.videoPlayer, { [styles.showVideoPlayer]: (show && videoLoaded) })}
          muted
          autoPlay
          loop
          crossOrigin='anonymous'
          onLoadedData={handleVideoLoaded}
          src={calculateSource(breakpoint)}
        />
      }
    </div>
  );
}

const videoViewEquals = function (prevProps, nextProps) {
  const paths = ['background'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const VideoView = React.memo(VideoViewComponent, videoViewEquals);

function getImageBackgroundStyles(imageCDN, background, isIcPageBackground, mobile) {
  var backgroundStyles = {};

  const media = get(background, 'media');
  var url = `${imageCDN}/tr:q-80/${media.url}`;
  var scale = get(background, 'scale', SCALE.SCALE_TO_FILL);
  var opacity = get(background, 'opacity', 100) / 100;

  if (!isIcPageBackground) {
    if(scale === SCALE.TILED) {
      url = `${imageCDN}/tr:q-80/${media.url}`;
    } else {
      url = `${imageCDN}/tr:q-80/${media.url}`;
      if (mobile){
        url = `${imageCDN}/tr:dpr-2,q-80/${media.url}`;
      }
    }
  }

  backgroundStyles = {
    ...backgroundStyles,
    backgroundImage: `url('${url}')`,
    opacity: opacity
  }

  switch (scale) {
    case SCALE.TILED:
      backgroundStyles = {
        ...backgroundStyles,
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto'
      }

      break;
    case SCALE.SCALE_TO_FILL:
    default:

      backgroundStyles = {
        ...backgroundStyles,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'
      }
      break;
  }

  const backgroundPosition = getBackgroundPosition(background);
  backgroundStyles = {
    ...backgroundStyles,
    backgroundPosition: backgroundPosition,
    height: '100%'
  }

  return backgroundStyles;
}

function getVideoBackgroundStyles(background) {
  var backgroundStyles = {};

  var opacity = get(background, 'opacity', 100) / 100;

  backgroundStyles = {
    ...backgroundStyles,
    opacity: opacity,
  }

  const backgroundPosition = getBackgroundPosition(background);
  backgroundStyles = {
    ...backgroundStyles,
    objectPosition: backgroundPosition,
  }

  return backgroundStyles;
}

function getVideoPosterStyles(background, url) {
  var backgroundStyles = {};

  const backgroundPosition = getBackgroundPosition(background);
  var opacity = get(background, 'opacity', 100) / 100;

  backgroundStyles = {
    ...backgroundStyles,
    backgroundImage: `url('${url}')`,
    backgroundPosition: backgroundPosition,
    opacity: opacity
  }

  return backgroundStyles;
}

function getBackgroundPosition(background) {
  var position = get(background, 'position', POSITION.CENTER);

  var backgroundPosition = 'center center';
  switch (position) {
    case POSITION.TOP_LEFT:
      backgroundPosition = 'left top';
      break;

    case POSITION.TOP_CENTER:
      backgroundPosition = 'center top';
      break;

    case POSITION.TOP_RIGHT:
      backgroundPosition = 'right top';
      break;

    case POSITION.CENTER_LEFT:
      backgroundPosition = 'left center';
      break;

    case POSITION.CENTER:
      backgroundPosition = 'center center';
      break;

    case POSITION.CENTER_RIGHT:
      backgroundPosition = 'right center';
      break;

    case POSITION.BOTTOM_LEFT:
      backgroundPosition = 'left bottom';
      break;

    case POSITION.BOTTOM_CENTER:
      backgroundPosition = 'center bottom';
      break;

    case POSITION.BOTTOM_RIGHT:
      backgroundPosition = 'right bottom';
      break;
  }

  return backgroundPosition;
}

export default BackgroundView;
